import { useState, useRef, useEffect, useCallback } from 'react'
import { useApi } from '../lib/api'
import type { Flag } from '../types'
import { FlagThumbnail } from './FlagThumbnail'

interface FlagPickerProps {
  value: Flag | null
  onChange: (f: Flag | null) => void
}

export function FlagPicker({ value, onChange }: FlagPickerProps) {
  const { apiFetch } = useApi()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [results, setResults] = useState<Flag[]>([])
  const [loading, setLoading] = useState(false)
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const search = useCallback((q: string) => {
    setLoading(true)
    const params = new URLSearchParams({ limit: '10' })
    if (q) params.set('q', q)
    apiFetch<{ flags: Flag[] }>(`/api/flags?${params}`)
      .then(({ flags }) => setResults(flags))
      .finally(() => setLoading(false))
  }, [apiFetch]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleInputChange(val: string) {
    setQuery(val)
    setOpen(true)
    if (debounce.current) clearTimeout(debounce.current)
    debounce.current = setTimeout(() => search(val), 250)
  }

  function handleFocus() {
    setOpen(true)
    if (results.length === 0) search(query)
  }

  function handleSelect(f: Flag) {
    onChange(f)
    setQuery('')
    setOpen(false)
  }

  function handleClear() {
    onChange(null)
    setQuery('')
    setResults([])
  }

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  if (value) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '6px 10px', border: '1px solid var(--color-border, #cbd5e1)',
        borderRadius: 6, background: 'var(--color-bg-alt, #f1f5f9)',
      }}>
        <FlagThumbnail id={value.id} type={value.type} imageUrl={value.image_url} name={value.name} width={40} height={25} />
        <span style={{ flex: 1, fontWeight: 500, fontSize: 13 }}>
          {value.name}
          {value.type === 'custom' && (
            <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--color-muted)', fontStyle: 'italic' }}>Eigene</span>
          )}
        </span>
        <button type="button" className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: 12 }} onClick={handleClear}>
          ✕
        </button>
      </div>
    )
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        type="search"
        placeholder="Flagge suchen…"
        value={query}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={handleFocus}
        autoComplete="off"
        style={{ width: '100%' }}
      />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: '#fff', border: '1px solid var(--color-border, #cbd5e1)',
          borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          maxHeight: 280, overflowY: 'auto',
        }}>
          {loading && <div style={{ padding: '10px 14px', color: 'var(--color-muted)', fontSize: 13 }}>Suche läuft…</div>}
          {!loading && results.length === 0 && (
            <div style={{ padding: '10px 14px', color: 'var(--color-muted)', fontSize: 13 }}>Keine Flaggen gefunden</div>
          )}
          {results.map((f) => (
            <div
              key={f.id}
              onMouseDown={() => handleSelect(f)}
              style={{
                padding: '7px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                borderBottom: '1px solid var(--color-border-light, #e2e8f0)',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '')}
            >
              <FlagThumbnail id={f.id} type={f.type} imageUrl={f.image_url} name={f.name} width={36} height={22} />
              <span style={{ fontWeight: 500, fontSize: 13 }}>{f.name}</span>
              {f.type === 'custom' && (
                <span style={{ fontSize: 11, color: 'var(--color-muted)', fontStyle: 'italic' }}>Eigene</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
